/**
 * Funnel session + lead state.
 *
 * Mint failures (ad blocker, offline, backend down) MUST NOT throw through
 * render. `useEnsureFunnelSession` surfaces them as `error`; `updateLead`
 * lazy-mints so page load triggers no backend call.
 */

import {
  getIntakeGetMyLeadQueryKey,
  intakeCreateSession,
  useIntakeGetMyLead,
  useIntakeUpdateMyLead,
} from '@/api/generated-catch-backend/funnel/funnel';
import type {
  FunnelLeadResponse,
  UpdateFunnelLeadRequest,
} from '@/api/generated-catch-backend/model';
import { FUNNEL_LEAD_KEY, FUNNEL_SESSION_ID_KEY, FUNNEL_SESSION_TOKEN_KEY, type PositioningVariant } from '@/constants/funnel';
import sentryService from '@/services/sentry.service';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';

interface UseFunnelLeadResult {
  lead: FunnelLeadResponse | undefined;
  isLoading: boolean;
  hasSession: boolean;
  updateLead: (update: UpdateFunnelLeadRequest) => Promise<FunnelLeadResponse>;
  isUpdating: boolean;
}

// Shared across callers so StrictMode double-mounts and parallel hook
// invocations don't race two POSTs. Cleared in `finally` so a retry isn't
// blocked by a settled-and-failed promise.
let inFlightMint: Promise<string> | null = null;

function reportMintFailure(error: unknown, positioningVariant?: PositioningVariant): void {
  const isNetworkError = axios.isAxiosError(error) && !error.response;
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const code = axios.isAxiosError(error) ? error.code : undefined;

  sentryService.captureException(error as Error, {
    operation: 'mintFunnelSession',
    positioningVariant: positioningVariant ?? 'none',
    isNetworkError,
    status,
    errorCode: code,
    // Set when the request never reached the server — usually an ad blocker
    // or privacy tool. Lets us triage iOS Safari + AdGuard from a real 5xx
    // without a session replay.
    suspectedClientBlock: isNetworkError,
  });
}

async function mintFunnelSession(positioningVariant?: PositioningVariant): Promise<string> {
  // StrictMode double-mounts can land a second caller here after the first
  // already wrote to localStorage — re-read before queuing a new request.
  const existing = localStorage.getItem(FUNNEL_SESSION_TOKEN_KEY);
  if (existing) return existing;

  if (inFlightMint) return inFlightMint;

  inFlightMint = (async () => {
    try {
      const response = await intakeCreateSession({
        positioningVariant: positioningVariant ?? null,
      });
      localStorage.setItem(FUNNEL_SESSION_TOKEN_KEY, response.funnelSessionToken);
      localStorage.setItem(FUNNEL_SESSION_ID_KEY, response.funnelSessionId);
      return response.funnelSessionToken;
    } catch (error) {
      reportMintFailure(error, positioningVariant);
      throw error;
    } finally {
      inFlightMint = null;
    }
  })();

  return inFlightMint;
}

async function ensureFunnelSession(positioningVariant?: PositioningVariant): Promise<string> {
  const existing = localStorage.getItem(FUNNEL_SESSION_TOKEN_KEY);
  if (existing) return existing;
  return mintFunnelSession(positioningVariant);
}

export function getFunnelSessionToken(): string | null {
  return localStorage.getItem(FUNNEL_SESSION_TOKEN_KEY);
}

export function getFunnelSessionId(): string | null {
  return localStorage.getItem(FUNNEL_SESSION_ID_KEY);
}

export function clearFunnelSessionToken(): void {
  localStorage.removeItem(FUNNEL_SESSION_TOKEN_KEY);
  localStorage.removeItem(FUNNEL_SESSION_ID_KEY);
  localStorage.removeItem(FUNNEL_LEAD_KEY);
}

/**
 * Read the cached funnel lead synchronously from localStorage. Returns
 * `null` if no lead has been written yet (first-time visitor) or if the
 * stored payload is malformed.
 *
 * This is the local source of truth for funnel step components. They read
 * it once in their `useState` initializer to pre-fill fields, then drive
 * their own controlled inputs. The server's lead at
 * `/v1/intake/session/me/lead` is a durable mirror that doesn't round-trip
 * back into UI state — avoiding the hydration race where a freshly-minted
 * funnel session triggers a server fetch that overwrites whatever the user
 * just typed.
 */
export function getFunnelLead(): FunnelLeadResponse | null {
  const raw = localStorage.getItem(FUNNEL_LEAD_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as FunnelLeadResponse;
  } catch {
    return null;
  }
}

function setFunnelLead(lead: FunnelLeadResponse): void {
  try {
    localStorage.setItem(FUNNEL_LEAD_KEY, JSON.stringify(lead));
  } catch {
    // Quota exceeded or serialization failure — non-fatal. The mutation's
    // React Query cache still holds the latest lead; localStorage is just
    // a refresh-survival cache.
  }
}

interface UseEnsureFunnelSessionResult {
  hasSession: boolean;
  isInitializing: boolean;
  error: Error | null;
  retry: () => void;
}

/**
 * Best-effort funnel session bootstrap. Callers MUST NOT gate render on
 * `hasSession` — render the funnel and show an error banner if `error` is
 * set. `positioningVariant` is ignored when a session already exists; the
 * variant was locked in at mint time.
 */
export function useEnsureFunnelSession(positioningVariant?: PositioningVariant): UseEnsureFunnelSessionResult {
  const [hasSession, setHasSession] = useState(() => Boolean(getFunnelSessionToken()));
  const [isInitializing, setIsInitializing] = useState(() => !getFunnelSessionToken());
  const [error, setError] = useState<Error | null>(null);
  // Bumping this re-fires the effect from a button handler.
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (hasSession) return;
    let cancelled = false;
    setIsInitializing(true);
    setError(null);
    void (async () => {
      try {
        await mintFunnelSession(positioningVariant);
        if (!cancelled) {
          setHasSession(true);
          setError(null);
        }
      } catch (mintError) {
        if (!cancelled) {
          setError(mintError instanceof Error ? mintError : new Error(String(mintError)));
        }
      } finally {
        if (!cancelled) {
          setIsInitializing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasSession, positioningVariant, retryNonce]);

  const retry = useCallback(() => {
    setRetryNonce((n) => n + 1);
  }, []);

  return { hasSession, isInitializing, error, retry };
}

export function useFunnelLead(): UseFunnelLeadResult {
  const queryClient = useQueryClient();
  const hasSession = Boolean(getFunnelSessionToken());

  const { data: lead, isLoading } = useIntakeGetMyLead({
    query: {
      enabled: hasSession,
      // Lead changes are driven by our own PATCHes — don't refetch on focus.
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  });

  // Mirror the GET response to localStorage too, so a returning visitor
  // whose localStorage was cleared (or whose session was minted on a
  // different device) refills the cache on first successful fetch.
  useEffect(() => {
    if (lead) setFunnelLead(lead);
  }, [lead]);

  const updateMutation = useIntakeUpdateMyLead({
    mutation: {
      onSuccess: (updated) => {
        queryClient.setQueryData(getIntakeGetMyLeadQueryKey(), updated);
        // Mirror to localStorage so funnel steps can pre-fill fields on
        // refresh / step navigation without a round-trip to the server.
        setFunnelLead(updated);
      },
    },
  });

  // Lazy-mint: nothing hits the backend until the user blurs their first
  // field. If mint fails, we never PATCH; the error propagates to the
  // caller's `catch`.
  const updateLead = useCallback(
    async (update: UpdateFunnelLeadRequest) => {
      await ensureFunnelSession();
      const result = await updateMutation.mutateAsync({ data: update });
      return result;
    },
    [updateMutation]
  );

  return {
    lead,
    isLoading: isLoading || !hasSession,
    hasSession,
    updateLead,
    isUpdating: updateMutation.isPending,
  };
}
